import { useState, type FormEvent } from "react";
import { Button } from "@cem/ui";
import { useAuth } from "./auth.js";
import { personalApp } from "./app-mode.js";

export const LoginPage = ({ mobile = false }: { mobile?: boolean }) => {
  const auth = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await auth.login(username, password);
    } catch {
      // The provider exposes a safe, user-facing error.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel" aria-labelledby="login-title">
        <img className="login-logo" src="/cem-logo.png" alt="CEM" />
        <p className="eyebrow">
          {personalApp ? "AREA PERSONALE" : "CEM EDILIZIA"}
        </p>
        <h1 id="login-title">
          {personalApp
            ? "Accesso amministratore"
            : mobile
              ? "Accedi ad App CEM"
              : "Bentornato"}
        </h1>
        {personalApp && (
          <p className="login-intro">
            Inserisci le credenziali personali dedicate.
          </p>
        )}
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Nome utente
            <input
              autoComplete="username"
              autoFocus
              maxLength={120}
              onChange={(event) => setUsername(event.target.value)}
              required
              value={username}
            />
          </label>
          <label>
            Password
            <span className="password-field">
              <input
                autoComplete="current-password"
                minLength={12}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={(event) =>
                  setCapsLock(event.getModifierState("CapsLock"))
                }
                onKeyUp={(event) =>
                  setCapsLock(event.getModifierState("CapsLock"))
                }
                required
                type={showPassword ? "text" : "password"}
                value={password}
              />
              <button
                aria-pressed={showPassword}
                className="password-visibility"
                onClick={() => setShowPassword((visible) => !visible)}
                type="button"
              >
                {showPassword ? "Nascondi" : "Mostra"}
              </button>
            </span>
          </label>
          {capsLock ? (
            <p className="caps-lock" role="status">
              Bloc Maiusc è attivo
            </p>
          ) : null}
          {auth.error === null ? null : (
            <p className="form-error" role="alert">
              {auth.error.message}
              {auth.error.reference === null ? null : (
                <small>Riferimento: {auth.error.reference}</small>
              )}
            </p>
          )}
          <Button disabled={submitting} tone="primary" type="submit">
            {submitting ? "Accesso…" : "Accedi"}
          </Button>
        </form>
        <details className="login-help">
          {!personalApp && (
            <p>
              <a
                href={mobile ? "/mobile/accesso/recupero" : "/accesso/recupero"}
              >
                Password o nome utente dimenticati?
              </a>
            </p>
          )}
          <summary>Problemi di accesso?</summary>
          <p>
            Evita tentativi ripetuti. Un operatore autorizzato può verificare
            l’account e rilasciare una password temporanea senza conoscere
            quella precedente.
          </p>
        </details>
        <p className="phase-note">
          {personalApp
            ? "Accesso protetto · Area personale amministratore"
            : "Accesso protetto"}
        </p>
      </section>
      {personalApp && (
        <aside className="login-scene" aria-hidden="true">
          <div className="scene-grid" />
          <div className="scene-copy">
            <span>Costruire bene.</span>
            <span>Gestire meglio.</span>
          </div>
        </aside>
      )}
    </main>
  );
};
